import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { FitScreenOutlined, MyLocationOutlined } from "@mui/icons-material";
import classNames from "classnames";
import { requestCameraPickingFit } from "../../../app/slices/GlobalSlice";
import { ISetCamera } from "../../../models/interactions/SetCamera";
import { SetCameraOption } from "../../../hooks/SetCameraOptions.hook";
import CameraPickerField from "./CameraPickerField";

/**
 * The slim bar shown while picking a camera, for a Set Camera interaction and for the
 * take's starting camera alike. The shot itself is docked in the map's corner
 * (Viewport3DCameraPicking), so the bar only names and switches the camera.
 * Below piLg it moves to the right so it clears the map tools in the bottom-left corner.
 */
export default function CameraPickingBar(props: {
  title: string;
  setCamera: ISetCamera;
  /** Replaces the default (update the selected Set Camera interaction). */
  onPick?: (option: SetCameraOption) => void;
  /** Only list cameras of this take location. */
  takeLocationId?: string;
  className?: string;
}) {
  const { title, setCamera, onPick, takeLocationId, className } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const fitLabel = String(t("set_camera_fit", "Fit all cameras in view"));

  return (
    <div
      className={classNames(
        "absolute bottom-[32px] right-[16px] piLg:right-auto piLg:left-1/2 piLg:-translate-x-1/2 flex items-center gap-[16px] py-[12px] pl-[24px] pr-[12px] rounded-[8px] bg-creator-primary-background shadow-md",
        className
      )}
      data-testid="camera-picking-bar"
    >
      <div className="whitespace-nowrap text-[14px] font-medium text-interaction-primary">
        {title}
      </div>
      <CameraPickerField setCamera={setCamera} onPick={onPick} takeLocationId={takeLocationId} />
      <div className="hidden piLg:flex items-center gap-[6px] text-[12px] text-creator-text-sub whitespace-nowrap">
        <MyLocationOutlined sx={{ fontSize: 14 }} />
        {t("set_camera_map_hint", "Or click a camera on the map")}
      </div>
      <button
        type="button"
        title={fitLabel}
        aria-label={fitLabel}
        onClick={() => dispatch(requestCameraPickingFit())}
        className="flex-none grid place-items-center w-[32px] h-[32px] rounded-[6px] text-creator-text-sub hover:bg-creator-surfaceHover hover:text-interaction-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-interaction-primary"
      >
        <FitScreenOutlined sx={{ fontSize: 20 }} />
      </button>
    </div>
  );
}
